import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Users, Mail, Loader2, Download, RefreshCw } from "lucide-react";
import { API } from "@/lib/constants";

export default function AdminPanel() {
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("zkn_token");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/subscribers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubs(data.subscribers || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("zkn_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    load();
  }, [token, navigate, load]);

  const logout = () => {
    localStorage.removeItem("zkn_token");
    navigate("/admin");
  };

  const exportCsv = () => {
    const rows = [["Imię", "E-mail", "Data zapisu"], ...subs.map((s) => [s.name || "", s.email, s.created_at])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "subskrybenci.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("pl-PL", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-beige" data-testid="admin-panel-page">
      <header className="bg-forest">
        <div className="w-full max-w-6xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-gold/20 text-gold font-outfit text-lg font-semibold">⟁</span>
            <span className="font-outfit text-lg font-semibold text-beige">Panel · Złap Kod Natury</span>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 text-beige/80 hover:text-gold transition-colors font-manrope text-sm" data-testid="admin-logout-button">
            <LogOut className="h-4 w-4" /> Wyloguj
          </button>
        </div>
      </header>

      <main className="w-full max-w-6xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-outfit text-3xl font-medium text-ink">Subskrybenci newslettera</h1>
            <p className="mt-2 font-manrope text-ink-soft inline-flex items-center gap-2">
              <Users className="h-4 w-4 text-gold" /> Łącznie zapisów: <strong className="text-forest">{subs.length}</strong>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="inline-flex items-center gap-2 border border-line bg-surface px-4 py-2.5 rounded-2xl font-outfit text-sm text-ink hover:border-gold transition-colors" data-testid="admin-refresh-button">
              <RefreshCw className="h-4 w-4" /> Odśwież
            </button>
            <button onClick={exportCsv} disabled={!subs.length} className="inline-flex items-center gap-2 bg-forest text-beige px-4 py-2.5 rounded-2xl font-outfit text-sm hover:bg-forest-hover transition-colors disabled:opacity-50" data-testid="admin-export-button">
              <Download className="h-4 w-4" /> Eksport CSV
            </button>
          </div>
        </div>

        <div className="mt-8 bg-surface border border-line rounded-2xl overflow-hidden shadow-clay">
          {loading ? (
            <div className="py-24 flex justify-center text-forest"><Loader2 className="h-7 w-7 animate-spin" /></div>
          ) : subs.length === 0 ? (
            <div className="py-24 text-center" data-testid="admin-empty-state">
              <Mail className="h-10 w-10 text-gold mx-auto" strokeWidth={1.25} />
              <p className="mt-4 font-manrope text-ink-soft">Brak zapisów. Pierwsi subskrybenci pojawią się tutaj.</p>
            </div>
          ) : (
            <table className="w-full text-left" data-testid="admin-subscribers-table">
              <thead>
                <tr className="border-b border-line bg-beige/50">
                  <th className="px-6 py-4 font-outfit text-xs uppercase tracking-wider text-ink-soft">#</th>
                  <th className="px-6 py-4 font-outfit text-xs uppercase tracking-wider text-ink-soft">Imię</th>
                  <th className="px-6 py-4 font-outfit text-xs uppercase tracking-wider text-ink-soft">E-mail</th>
                  <th className="px-6 py-4 font-outfit text-xs uppercase tracking-wider text-ink-soft">Data zapisu</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s, i) => (
                  <tr key={s.id || i} className="border-b border-line/60 hover:bg-beige/40 transition-colors" data-testid={`admin-subscriber-row-${i}`}>
                    <td className="px-6 py-4 font-manrope text-sm text-ink-soft">{i + 1}</td>
                    <td className="px-6 py-4 font-manrope text-sm text-ink">{s.name || "—"}</td>
                    <td className="px-6 py-4 font-manrope text-sm text-forest">{s.email}</td>
                    <td className="px-6 py-4 font-manrope text-sm text-ink-soft">{fmt(s.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
